// Browser push notifications via Firebase Cloud Messaging.
//
// The same FCM project as the mobile apps (cloudloop-2b702), so the backend's
// existing sender reaches browsers too: this module gets an FCM token for the
// browser and registers it with POST /api/device_token {platform: 'web'},
// exactly like the app. Delivery and click-through live in
// public/firebase-messaging-sw.js.
//
// The Firebase SDK is loaded lazily — only when enabling or resyncing — so it
// never weighs on the main bundle. Everything is inert until the web app's
// config and VAPID key below are filled in (Firebase console → Project
// settings → Your apps → Web app; Cloud Messaging → Web Push certificates).
// All of these values are public by design.

const FIREBASE_CONFIG = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY || "",
  appId: process.env.VUE_APP_FIREBASE_APP_ID || "",
  projectId: "cloudloop-2b702",
  messagingSenderId: "733383931517",
};
const VAPID_KEY = process.env.VUE_APP_FIREBASE_VAPID_KEY || "";

const SW_URL = "/firebase-messaging-sw.js";
// Firebase's own default scope: isolates this worker from the app's.
const SW_SCOPE = "/firebase-cloud-messaging-push-scope";
const ENABLED_KEY = "webPushEnabled";
const TOKEN_KEY = "webPushToken";

const API = process.env.VUE_APP_API_URL || "";

function storageGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}
function storageSet(key, value) {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch (e) {
    /* storage unavailable — push still works for this page load */
  }
}

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${storageGet("JWT")}`,
  };
}

/** Configured, and the browser can do web push at all. (iOS Safari only
 *  inside a Home-Screen-installed web app.) */
export function isSupported() {
  return !!(
    FIREBASE_CONFIG.apiKey &&
    FIREBASE_CONFIG.appId &&
    VAPID_KEY &&
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** On for this browser: the user enabled it and permission still stands. */
export function isEnabled() {
  return (
    isSupported() &&
    storageGet(ENABLED_KEY) === "1" &&
    Notification.permission === "granted"
  );
}

export function permissionDenied() {
  return typeof Notification !== "undefined" && Notification.permission === "denied";
}

let messagingPromise = null;
function messaging() {
  if (!messagingPromise) {
    messagingPromise = Promise.all([
      import(/* webpackChunkName: "firebase" */ "firebase/app"),
      import(/* webpackChunkName: "firebase" */ "firebase/messaging"),
    ]).then(([{ initializeApp }, fm]) => ({
      fm,
      instance: fm.getMessaging(initializeApp(FIREBASE_CONFIG)),
    }));
  }
  return messagingPromise;
}

async function currentToken() {
  const registration = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  const { fm, instance } = await messaging();
  return fm.getToken(instance, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
}

async function registerWithServer(token) {
  const resp = await fetch(`${API}/api/device_token`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
    body: JSON.stringify({ token, platform: "web" }),
  });
  if (!resp.ok) throw new Error(`device_token ${resp.status}`);
  storageSet(TOKEN_KEY, token);
}

/** Turn push on for this browser. Must run from a user gesture (Safari and
 *  Firefox only prompt for permission inside one). Resolves true on success. */
export async function enable() {
  if (!isSupported()) return false;
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return false;
  const token = await currentToken();
  if (!token) return false;
  await registerWithServer(token);
  storageSet(ENABLED_KEY, "1");
  return true;
}

/** Turn push off: drop the server row, then the browser's FCM token. */
export async function disable() {
  const token = storageGet(TOKEN_KEY);
  storageSet(ENABLED_KEY, null);
  storageSet(TOKEN_KEY, null);
  if (!isSupported()) return;
  if (token) {
    await fetch(`${API}/api/device_token`, {
      method: "DELETE",
      credentials: "include",
      headers: authHeaders(),
      body: JSON.stringify({ token }),
    }).catch(() => {});
  }
  try {
    const { fm, instance } = await messaging();
    await fm.deleteToken(instance);
  } catch (e) {
    /* already gone */
  }
}

/** Call after login / app start. Re-registers so a token the server pruned
 *  (or that FCM rotated) is restored without the user doing anything.
 *  No prompt — does nothing unless push is already on. */
export async function resync() {
  if (!isEnabled()) return;
  try {
    const token = await currentToken();
    if (token) await registerWithServer(token);
  } catch (e) {
    console.warn("Web push resync failed", e);
  }
}

// Deep links in pushes are the server's share paths (/s/, /u/, /invite/, /p/);
// map them to the SPA's hash routes so an open tab can route without a
// reload. null = not an app route (the caller does a full navigation).
export function linkToRoute(link) {
  let url;
  try {
    url = new URL(link, window.location.origin);
  } catch (e) {
    return null;
  }
  if (url.origin !== window.location.origin) return null;
  const [, kind, ...rest] = url.pathname.split("/");
  const arg = rest.join("/");
  if (!arg) return null;
  const route = { s: "/session/", u: "/users/", invite: "/invite/", p: "/pack/" }[kind];
  return route ? route + arg : null;
}

/** A notification click on an already-open tab: the worker focuses it and
 *  posts the link; route there. Call once at app start. */
export function listenForNotificationClicks(router) {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) return;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (!event.data || event.data.type !== "cloudloop-open") return;
    const route = linkToRoute(event.data.link);
    if (route) router.push(route);
    else window.location.href = event.data.link;
  });
  // Deliver messages now (they're queued until the page opts in).
  navigator.serviceWorker.startMessages();
}
